import { Download } from '@crehana/compass.icons.outline/v2/download';
import { FollowUp } from '@crehana/compass.icons.outline/v2/follow-up';
import Loader from '@crehana/compass.ui/loader';
import { TooltipWrapper } from '@crehana/compass.ui/tooltip';

import { useHomeTranslations } from '../../../hooks/use-home-translations';
import type { PayrollReceipt } from '../types';
import { formatReceiptDateRange } from '../utils/format-payroll-date';

type PayrollReceiptItemProps = {
  receipt: PayrollReceipt;
  onViewReceipt: (receipt: PayrollReceipt) => void;
  onDownloadReceipt: (receipt: PayrollReceipt) => void;
  isDownloading?: boolean;
};

export function PayrollReceiptItem({
  receipt,
  onViewReceipt,
  onDownloadReceipt,
  isDownloading = false,
}: PayrollReceiptItemProps) {
  const { t, languageCode } = useHomeTranslations();
  const dateRange = formatReceiptDateRange(
    receipt.startDate,
    receipt.endDate,
    languageCode,
  );
  const periodLabel = `${t('PAYROLL_PERIOD_LABEL')} ${receipt.periodNumber} | ${
    receipt.paymentType
  }`;

  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-cds-gap-space-04 tw-cds-p-space-04 tw-border-b tw-border-solid tw-cds-border-divider last:tw-border-b-0 hover:tw-cds-bg-subtle-01">
      <div className="tw-min-w-0 tw-flex-1 tw-flex tw-flex-col tw-gap-space-02">
        <p className="tw-cds-font-subtitle-01 tw-font-semibold tw-cds-mb-space-02">
          {dateRange}
        </p>
        <p className="tw-cds-font-body-01 tw-cds-text-subtle">{periodLabel}</p>
      </div>
      <div className="tw-flex tw-shrink-0 tw-cds-gap-space-02">
        <TooltipWrapper
          value={t('PAYROLL_VIEW_RECEIPT')}
          side="top"
          align="center"
          delayDuration={100}
          asChild
        >
          <button
            type="button"
            className="tw-border tw-border-solid tw-border-[var(--cds-border-neutral)] hover:tw-border-[var(--cds-button-border-secondary-hover)] tw-cds-radius-s tw-cds-w-32 tw-cds-h-32 tw-flex tw-items-center tw-justify-center"
            onClick={() => onViewReceipt(receipt)}
            aria-label={t('PAYROLL_VIEW_RECEIPT')}
          >
            <FollowUp
              size={20}
              className="tw-shrink-0 tw-fill-current hover:tw-text-[var(--cds-button-content-secondary-hover)]"
            />
          </button>
        </TooltipWrapper>
        <TooltipWrapper
          value={t('PAYROLL_DOWNLOAD_RECEIPT_TOOLTIP')}
          side="top"
          align="center"
          delayDuration={100}
          asChild
        >
          <button
            type="button"
            className="tw-bg-[var(--cds-button-bg-primary)] hover:tw-bg-[var(--cds-button-bg-primary-hover)] tw-cds-w-32 tw-cds-h-32 tw-flex tw-items-center tw-justify-center tw-cds-radius-s"
            onClick={() => onDownloadReceipt(receipt)}
            disabled={isDownloading}
            aria-label={t('PAYROLL_DOWNLOAD_RECEIPT_TOOLTIP')}
          >
            {isDownloading ? (
              <Loader size="xs" className="tw-dark" />
            ) : (
              <Download
                size={16}
                className="tw-cds-text-inverse tw-fill-current"
              />
            )}
          </button>
        </TooltipWrapper>
      </div>
    </div>
  );
}
